import { BlobReader, ZipReader } from '@zip.js/zip.js';

export const ZIP_IMPORT_SIZE_LIMIT_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * CP437 (DOS) character map for bytes 128-255.
 * JavaScript's TextDecoder doesn't support CP437, so we need a manual mapping.
 * This is essential for ZIP files created on Windows with Danish/Nordic characters.
 */
const CP437_HIGH_CHARS = [
  'Ç',
  'ü',
  'é',
  'â',
  'ä',
  'à',
  'å',
  'ç',
  'ê',
  'ë',
  'è',
  'ï',
  'î',
  'ì',
  'Ä',
  'Å',
  'É',
  'æ',
  'Æ',
  'ô',
  'ö',
  'ò',
  'û',
  'ù',
  'ÿ',
  'Ö',
  'Ü',
  '¢',
  '£',
  '¥',
  '₧',
  'ƒ',
  'á',
  'í',
  'ó',
  'ú',
  'ñ',
  'Ñ',
  'ª',
  'º',
  '¿',
  '⌐',
  '¬',
  '½',
  '¼',
  '¡',
  '«',
  '»',
  '░',
  '▒',
  '▓',
  '│',
  '┤',
  '╡',
  '╢',
  '╖',
  '╕',
  '╣',
  '║',
  '╗',
  '╝',
  '╜',
  '╛',
  '┐',
  '└',
  '┴',
  '┬',
  '├',
  '─',
  '┼',
  '╞',
  '╟',
  '╚',
  '╔',
  '╩',
  '╦',
  '╠',
  '═',
  '╬',
  '╧',
  '╨',
  '╤',
  '╥',
  '╙',
  '╘',
  '╒',
  '╓',
  '╫',
  '╪',
  '┘',
  '┌',
  '█',
  '▄',
  '▌',
  '▐',
  '▀',
  'α',
  'ß',
  'Γ',
  'π',
  'Σ',
  'σ',
  'µ',
  'τ',
  'Φ',
  'Θ',
  'Ω',
  'δ',
  '∞',
  'φ',
  'ε',
  '∩',
  '≡',
  '±',
  '≥',
  '≤',
  '⌠',
  '⌡',
  '÷',
  '≈',
  '°',
  '∙',
  '·',
  '√',
  'ⁿ',
  '²',
  '■',
  ' ',
];

/**
 * Decodes filename bytes with proper encoding detection.
 * Supports UTF-8, CP437 (DOS), windows-1252, and ISO-8859-1.
 * Similar to PHP's asUTF8() / asEncoding() method.
 *
 * @param bytes - Raw bytes from ZIP file
 * @param encoding - Suggested encoding from ZIP file (often 'cp437' or 'utf-8')
 * @returns Properly decoded UTF-8 string
 */
function decodeFilename(bytes: Uint8Array, encoding: string): string {
  // Always try UTF-8 first (many modern ZIPs use UTF-8 even if marked as cp437)
  try {
    const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
    const decoded = utf8Decoder.decode(bytes);
    // If we got here, bytes are valid UTF-8
    // Double-check: valid UTF-8 text shouldn't have byte sequences that are
    // much longer than the resulting string length
    const reEncoded = new TextEncoder().encode(decoded);
    if (reEncoded.length === bytes.length) {
      return decoded;
    }
  } catch {
    // UTF-8 decoding failed, continue to encoding-specific handling
  }

  // Handle CP437 encoding (common in Windows ZIP files with Nordic/special chars)
  if (encoding === 'cp437') {
    let decoded = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      if (byte < 128) {
        // ASCII range (0-127) is the same in all encodings
        decoded += String.fromCharCode(byte);
      } else {
        // Extended ASCII range (128-255) needs CP437 mapping
        decoded += CP437_HIGH_CHARS[byte - 128];
      }
    }
    return decoded;
  }

  // For other encodings or if CP437 fails, try windows-1252/ISO-8859-1
  try {
    const win1252Decoder = new TextDecoder('windows-1252');
    return win1252Decoder.decode(bytes);
  } catch {
    // Final fallback: ISO-8859-1 (always succeeds, maps bytes 1:1)
    const isoDecoder = new TextDecoder('iso-8859-1');
    return isoDecoder.decode(bytes);
  }
}

/**
 * Reads the Central Directory of a ZIP file without decompressing file content.
 * Returns a deduplicated, sorted list of folder paths (no trailing slash).
 * Filters out macOS metadata (__MACOSX) entries.
 */
export async function readZipFolderPaths(file: File): Promise<string[]> {
  const reader = new ZipReader(new BlobReader(file));

  try {
    // Use decodeText to intercept and properly decode filenames
    const entries = await reader.getEntries({
      decodeText: (bytes: Uint8Array, encoding: string) => {
        return decodeFilename(bytes, encoding);
      },
    });

    const folderSet = new Set<string>();

    entries.forEach((entry) => {
      const rawPath = entry.filename;

      // Collect explicit directory entries
      if (entry.directory) {
        const path = rawPath.replace(/\/$/, '').trim();
        if (path) folderSet.add(path);
        return;
      }

      // Derive implicit parent folders from file entries
      const parts = rawPath.split('/').filter(Boolean);
      if (parts.length > 1) {
        for (let i = 1; i < parts.length; i++) {
          folderSet.add(parts.slice(0, i).join('/'));
        }
      }
    });

    return Array.from(folderSet)
      .filter((p) => !p.startsWith('__MACOSX') && !p.includes('/__MACOSX'))
      .sort((a, b) => {
        const aDepth = a.split('/').length;
        const bDepth = b.split('/').length;
        if (aDepth !== bDepth) return aDepth - bDepth;
        return a.localeCompare(b);
      });
  } finally {
    await reader.close();
  }
}

/**
 * Given a flat list of folder paths, expands each path to include all
 * implicit parent paths. Returns a deduplicated, depth-sorted list.
 */
export function expandFolderPaths(paths: string[]): string[] {
  const allPaths = new Set<string>();

  paths.forEach((path) => {
    const segments = path.split('/').filter(Boolean);
    for (let i = 1; i <= segments.length; i++) {
      allPaths.add(segments.slice(0, i).join('/'));
    }
  });

  return Array.from(allPaths).sort((a, b) => {
    const aDepth = a.split('/').length;
    const bDepth = b.split('/').length;
    if (aDepth !== bDepth) return aDepth - bDepth;
    return a.localeCompare(b);
  });
}
